import { useEffect, useRef, useState } from 'react';
import { Play, Pause, Download, Loader2 } from 'lucide-react';
import { downloadBlob, formatDuration } from '../../lib/utils';

/**
 * Waveform audio player. Decodes the track once to draw a bar waveform, then
 * paints the played portion in brand color. Click the waveform to seek.
 */
export default function WaveformPlayer({ src, filename = 'aether-track.flac' }) {
  const canvasRef = useRef(null);
  const audioRef = useRef(null);
  const peaksRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  // Decode audio -> peaks
  useEffect(() => {
    let cancelled = false;
    setReady(false);
    (async () => {
      try {
        const res = await fetch(src);
        const arr = await res.arrayBuffer();
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const buffer = await ctx.decodeAudioData(arr);
        const raw = buffer.getChannelData(0);
        const BARS = 96;
        const block = Math.floor(raw.length / BARS) || 1;
        const peaks = [];
        for (let i = 0; i < BARS; i++) {
          let sum = 0;
          for (let j = 0; j < block; j++) sum += Math.abs(raw[i * block + j] || 0);
          peaks.push(sum / block);
        }
        const max = Math.max(...peaks, 0.0001);
        peaksRef.current = peaks.map((p) => p / max);
        ctx.close();
        if (!cancelled) {
          setReady(true);
          draw(0);
        }
      } catch {
        // Decoding can fail for some formats/CORS — fall back to native controls.
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const draw = (progress) => {
    const canvas = canvasRef.current;
    const peaks = peaksRef.current;
    if (!canvas || !peaks) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const gap = 2;
    const barW = w / peaks.length - gap;
    peaks.forEach((p, i) => {
      const barH = Math.max(2, p * h * 0.9);
      const x = i * (barW + gap);
      const y = (h - barH) / 2;
      ctx.fillStyle = i / peaks.length <= progress ? '#6d5efc' : 'rgba(255,255,255,0.16)';
      ctx.beginPath();
      ctx.roundRect(x, y, barW, barH, 2);
      ctx.fill();
    });
  };

  // Repaint on progress
  useEffect(() => {
    if (duration) draw(current / duration);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, duration, ready]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) a.pause();
    else a.play();
  };

  const onSeek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = (e.clientX - rect.left) / rect.width;
    a.currentTime = ratio * duration;
  };

  return (
    <div className="glass rounded-xl p-4">
      <audio
        ref={audioRef}
        src={src}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
      >
        <track kind="captions" />
      </audio>

      <div className="flex items-center gap-4">
        <button
          onClick={toggle}
          disabled={!ready}
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-brand-500 text-white shadow-glow transition-transform hover:scale-105 active:scale-95 disabled:opacity-60"
          aria-label={playing ? 'Pause' : 'Play'}
        >
          {!ready ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : playing ? (
            <Pause className="h-5 w-5" />
          ) : (
            <Play className="ml-0.5 h-5 w-5" />
          )}
        </button>

        <div className="min-w-0 flex-1">
          <canvas
            ref={canvasRef}
            onClick={onSeek}
            className="h-14 w-full cursor-pointer"
          />
          <div className="mt-1 flex justify-between font-mono text-xs text-content-subtle">
            <span>{formatDuration(current)}</span>
            <span>{formatDuration(duration)}</span>
          </div>
        </div>

        <button
          onClick={async () => {
            const res = await fetch(src);
            downloadBlob(await res.blob(), filename);
          }}
          className="shrink-0 rounded-lg p-2 text-content-subtle transition-colors hover:bg-white/5 hover:text-content"
          aria-label="Download track"
        >
          <Download className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
